import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addMessage, addDanmaku, setViewerCount, setIsLive, clearMessages } from '../store/chatSlice';
import VideoPlayer from '../components/VideoPlayer';
import ChatSidebar from '../components/ChatSidebar';
import Controls from '../components/Controls';
import Danmaku from '../components/Danmaku';

// WebSocket 地址（通过 Worker 处理，因为 Pages Functions 不支持 WebSocket）
const WS_BASE = window.location.hostname === 'localhost'
  ? `ws://localhost:8787`
  : `wss://livestream-api.19126971307.workers.dev`;
const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

function LiveRoom({ token, user, onLogout }) {
  const { roomId: urlRoomId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isHost, setIsHost] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [roomName, setRoomName] = useState('');
  const [roomId, setRoomId] = useState(urlRoomId || '');
  const [joined, setJoined] = useState(false);
  const [danmakuInput, setDanmakuInput] = useState('');

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);

  // ── 检测 HTTPS ──────────────────────────────
  const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

  // ── WebSocket 连接 ──────────────────────────
  const connectWS = useCallback((rid, role) => {
    const userId = user?.id || user?.username || 'anonymous';
    const ws = new WebSocket(`${WS_BASE}/ws/${rid}?userId=${encodeURIComponent(userId)}&role=${role}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[WS] 已连接到房间:', rid);
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      switch (msg.type) {
        case 'user-joined':
          dispatch(setViewerCount(msg.viewerCount));
          if (msg.role !== 'host' && isHost) {
            // 有新观众加入，准备创建 peer connection
            createPeerConnection(msg.userId);
          }
          break;

        case 'user-left':
          dispatch(setViewerCount(msg.viewerCount));
          break;

        case 'host-left':
          dispatch(addMessage({ content: '主播已离开', type: 'system', sender: '系统' }));
          dispatch(setIsLive(false));
          break;

        case 'offer':
          handleOffer(msg.offer, msg.from);
          break;

        case 'answer':
          handleAnswer(msg.answer);
          break;

        case 'ice-candidate':
          handleIceCandidate(msg.candidate);
          break;

        case 'chat':
          dispatch(addMessage({ content: msg.content, type: 'chat', sender: msg.userId }));
          break;

        case 'danmaku':
          dispatch(addDanmaku({ id: msg.id || Date.now(), content: msg.content, color: msg.color }));
          break;

        case 'room-info':
          dispatch(setViewerCount(msg.viewerCount));
          break;

        default:
          break;
      }
    };

    ws.onclose = () => console.log('[WS] 断开连接');
    ws.onerror = (err) => console.error('[WS] 错误:', err);
  }, [user, isHost, dispatch]);

  // ── WebRTC: 创建 PeerConnection ─────────────
  const createPeerConnection = useCallback((targetId) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pcRef.current = pc;

    // 添加本地流
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // ICE candidate
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice-candidate',
          candidate: event.candidate,
          target: isHost ? targetId : 'host',
        }));
      }
    };

    // 收到远程流
    pc.ontrack = (event) => {
      setRemoteStream(event.streams[0]);
    };

    // 如果是主播，创建 offer
    if (isHost) {
      pc.createOffer().then((offer) => {
        pc.setLocalDescription(offer);
        wsRef.current?.send(JSON.stringify({
          type: 'offer',
          offer,
          target: targetId,
        }));
      });
    }

    return pc;
  }, [isHost]);

  // ── WebRTC: 处理 offer（观众端） ────────────
  const handleOffer = useCallback(async (offer, fromId) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pcRef.current = pc;

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice-candidate',
          candidate: event.candidate,
          target: 'host',
        }));
      }
    };

    pc.ontrack = (event) => {
      setRemoteStream(event.streams[0]);
    };

    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    wsRef.current?.send(JSON.stringify({
      type: 'answer',
      answer,
      target: 'host',
    }));
  }, []);

  // ── WebRTC: 处理 answer（主播端） ───────────
  const handleAnswer = useCallback(async (answer) => {
    if (pcRef.current) {
      await pcRef.current.setRemoteDescription(new RTCSessionDescription(answer));
    }
  }, []);

  // ── WebRTC: 处理 ICE candidate ──────────────
  const handleIceCandidate = useCallback(async (candidate) => {
    if (pcRef.current) {
      try {
        await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error('ICE candidate 错误:', err);
      }
    }
  }, []);

  // ── 开始直播（主播端） ──────────────────────
  const handleStart = async () => {
    if (!isSecure) {
      alert('WebRTC 需要 HTTPS 或 localhost 环境');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(stream);
      localStreamRef.current = stream;

      // 生成房间 ID（游客模式直接本地生成）
      const newRoomId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      setRoomId(newRoomId);
      setIsHost(true);
      dispatch(setIsLive(true));
      connectWS(newRoomId, 'host');
      setJoined(true);
      navigate(`/room/${newRoomId}`);
    } catch (err) {
      console.error('开始直播失败:', err);
      alert('无法获取摄像头/麦克风权限');
    }
  };

  // ── 加入直播间（观众端） ──────────────────────
  const handleJoin = () => {
    if (!roomId) return;
    setIsHost(false);
    connectWS(roomId, 'viewer');
    setJoined(true);
    navigate(`/room/${roomId}`);
  };

  // ── 结束直播 ────────────────────────────────
  const handleStop = () => {
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
      localStreamRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    dispatch(setIsLive(false));
    setJoined(false);
    setRemoteStream(null);

  };

  // ── 切换麦克风 ──────────────────────────────
  const handleToggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };

  // ── 切换摄像头 ──────────────────────────────
  const handleToggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };

  // ── 发送弹幕 ────────────────────────────────
  const handleSendDanmaku = () => {
    const content = danmakuInput.trim();
    if (!content || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    // 先本地显示自己的弹幕（服务器广播会排除发送者）
    dispatch(addDanmaku({
      id: Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      content,
      color: '#ffffff',
    }));

    wsRef.current.send(JSON.stringify({
      type: 'danmaku',
      content,
      color: '#ffffff',
    }));
    setDanmakuInput('');
  };

  // ── 清理 ────────────────────────────────────
  useEffect(() => {
    return () => {
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
      if (pcRef.current) pcRef.current.close();
      if (wsRef.current) wsRef.current.close();
      dispatch(clearMessages());
    };
  }, []); // eslint-disable-line

  // ── 未加入时显示大厅 ──────────────────────────
  if (!joined) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="bg-dark-800 p-8 rounded-lg shadow-lg w-96">
          <h1 className="text-2xl font-bold mb-6 text-center">🎬 直播大厅</h1>

          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">创建直播间</h2>
            <input
              type="text"
              placeholder="直播间名称"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              className="w-full p-3 mb-3 bg-dark-700 rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
            />
            <button
              onClick={handleStart}
              className="w-full p-3 bg-red-600 hover:bg-red-700 rounded font-semibold transition"
            >
              🎙️ 开始直播
            </button>
          </div>

          <div className="border-t border-gray-700 pt-6">
            <h2 className="text-lg font-semibold mb-3">加入直播间</h2>
            <input
              type="text"
              placeholder="输入房间 ID"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              className="w-full p-3 mb-3 bg-dark-700 rounded border border-gray-600 focus:border-green-500 focus:outline-none"
            />
            <button
              onClick={handleJoin}
              className="w-full p-3 bg-green-600 hover:bg-green-700 rounded font-semibold transition"
            >
              👁️ 加入观看
            </button>
          </div>

          <div className="mt-4 text-center">
            <button onClick={onLogout} className="text-gray-400 hover:text-white text-sm">
              退出登录
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── 复制房间号 ──────────────────────────────
  const [copied, setCopied] = useState(false);
  const handleCopyRoomId = () => {
    const link = `${window.location.origin}/room/${roomId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── 直播间主界面 ──────────────────────────────
  return (
    <div className="min-h-screen bg-dark-900 p-4">
      <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-2rem)]">
        {/* 左侧：视频区域 (70%) */}
        <div className="lg:w-[70%] flex flex-col gap-4">
          {/* 房间信息栏 */}
          <div className="flex items-center justify-between bg-dark-800 rounded-lg px-4 py-2">
            <div className="flex items-center gap-3">
              <span className="text-red-500 font-bold">● LIVE</span>
              <span className="text-gray-400 text-sm">房间号：</span>
              <span className="bg-dark-700 px-3 py-1 rounded font-mono text-sm text-white select-all">
                {roomId}
              </span>
            </div>
            <button
              onClick={handleCopyRoomId}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm transition"
            >
              {copied ? '✅ 已复制' : '📋 复制链接'}
            </button>
          </div>

          {/* 视频播放器 + 弹幕层 */}
          <div className="relative flex-1">
            <VideoPlayer localStream={localStream} remoteStream={remoteStream} isHost={isHost} />
            <Danmaku />
          </div>

          {/* 弹幕输入 */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="发弹幕..."
              value={danmakuInput}
              onChange={(e) => setDanmakuInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendDanmaku()}
              className="flex-1 p-2 bg-dark-800 rounded border border-gray-700 focus:border-purple-500 focus:outline-none text-sm"
            />
            <button
              onClick={handleSendDanmaku}
              className="px-4 bg-purple-600 hover:bg-purple-700 rounded text-sm font-semibold transition"
            >
              弹幕
            </button>
          </div>

          {/* 控制按钮 */}
          <Controls
            isLive={localStream !== null}
            isHost={isHost}
            audioEnabled={audioEnabled}
            videoEnabled={videoEnabled}
            onStart={handleStart}
            onStop={handleStop}
            onToggleAudio={handleToggleAudio}
            onToggleVideo={handleToggleVideo}
          />
        </div>

        {/* 右侧：聊天栏 (30%) */}
        <div className="lg:w-[30%]">
          <ChatSidebar token={token} roomId={roomId} user={user} />
        </div>
      </div>
    </div>
  );
}

export default LiveRoom;
